import { TravelPremiumResponse } from '@app/domain/model/eshop/travel/TravelPremiumResponse';
import { TravelRiskPackageCode } from '@app/domain/model/eshop/travel/TravelRiskPackageCode';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';

const REQUIRED_RISK_CODES: string[] = [TravelRiskPackageCode.MedicalExpenses];

export function usePriceCalculation(
    currentPremiums: Nullable<TravelPremiumResponse>,
    initialSelectedRiskCodes?: string[],
) {
    const [selectedRiskCodes, setSelectedRiskCodes] = useState<string[]>(
        initialSelectedRiskCodes ?? REQUIRED_RISK_CODES,
    );

    const selectedRisks = useMemo(() => {
        if (!currentPremiums) return [];

        return currentPremiums.risks.filter(risk => risk.code && selectedRiskCodes.includes(risk.code));
    }, [currentPremiums, selectedRiskCodes]);

    const totalPrice = useMemo(() => {
        if (!selectedRisks) return 0;

        return selectedRisks.reduce((total, risk) => {
            const price =
                risk.premiumAfterDiscount && risk.premiumAfterDiscount > 0
                    ? risk.premiumAfterDiscount
                    : risk.premium || 0;
            return total + price;
        }, 0);
    }, [selectedRisks]);

    const handleRisksChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        const { value, checked } = e.target;

        if (!checked && REQUIRED_RISK_CODES.includes(value)) {
            return;
        }

        setSelectedRiskCodes(prev => {
            if (checked) {
                return [...prev, value];
            } else {
                return prev.filter(code => code !== value);
            }
        });
    }, []);

    const updateSelectedRisks = useCallback(() => {
        setSelectedRiskCodes(prev => {
            const filtered = prev.filter(code => currentPremiums?.risks.some(risk => risk.code === code));
            const allRequired = REQUIRED_RISK_CODES.filter(
                code => !filtered.includes(code) && currentPremiums?.risks.some(risk => risk.code === code),
            );
            return [...filtered, ...allRequired];
        });
    }, [currentPremiums]);

    const initializeFromSession = useCallback((riskCodes: string[]) => {
        setSelectedRiskCodes(riskCodes);
    }, []);

    return {
        priceState: {
            selectedRisks,
            totalPrice,
        },
        handleRisksChange,
        updateSelectedRisks,
        initializeFromSession,
    };
}
